import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonService } from '../../../services/common.service';
import { CognitoService } from '../../../services/cognito.service';
import { NotificationService } from '../../../services/notification.service';
import { normalizeConceptDataFilter } from '../../../shared/appUtil';
import * as Types from '../../../shared/types';

export interface ConceptFilterDialogResult {
  filters: Types.ConceptDataFilter[];
  combineWith: 'OR' | 'AND';
}

interface SuggestionRow extends Types.ConceptSuggestion {
  selected: boolean;
}

@Component({
  selector: 'app-concept-filter-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './concept-filter-dialog.component.html',
  styleUrl: './concept-filter-dialog.component.scss',
})
export class ConceptFilterDialogComponent implements OnInit {
  conceptText = '';
  loadingSuggest = false;
  loadingSave = false;
  warnings: string[] = [];
  phrases: string[] = [];
  combineWith: 'OR' | 'AND' = 'OR';
  suggestions: SuggestionRow[] = [];
  groupedSuggestions: { group: string; items: SuggestionRow[] }[] = [];

  showSaveForm = false;
  saveName = '';
  saveDescription = '';
  isGlobal = true;
  lastConcept = '';

  constructor(
    private dialogRef: MatDialogRef<ConceptFilterDialogComponent, ConceptFilterDialogResult | undefined>,
    private commonService: CommonService,
    private cognitoService: CognitoService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {}

  get selectedSuggestions(): SuggestionRow[] {
    return this.suggestions.filter((s) => s.selected);
  }

  get canOk(): boolean {
    return this.selectedSuggestions.length > 0 && !this.loadingSuggest;
  }

  get canSuggest(): boolean {
    return this.conceptText.trim().length > 0 && !this.loadingSuggest;
  }

  handleSuggest(): void {
    if (!this.canSuggest) {
      return;
    }

    this.loadingSuggest = true;
    this.warnings = [];
    this.suggestions = [];
    this.groupedSuggestions = [];

    const body: Types.ConceptSuggestRequest = {
      concept: this.conceptText.trim(),
      topKPerField: 5,
      minSimilarity: 0.55,
      includeTextFallback: true,
      extractPhrases: true,
    };

    this.commonService.suggestConceptFilters(body).subscribe({
      next: (response) => {
        const data: Types.ConceptSuggestResponse = response?.data ?? response;
        this.lastConcept = data?.concept ?? this.conceptText.trim();
        this.combineWith = (data?.combineWith as 'OR' | 'AND') || 'OR';
        this.warnings = data?.warnings ?? [];
        this.phrases = data?.phrases ?? [];
        this.suggestions = (data?.suggestions ?? []).map((s) => ({
          ...s,
          selected: !!s.selectedByDefault,
        }));
        this.rebuildGroups();
        this.loadingSuggest = false;

        if (!this.suggestions.length) {
          this.notificationService.info('Concept filter', 'No suggestions found for this concept.');
        }
      },
      error: (error) => {
        this.loadingSuggest = false;
        const message =
          error?.error?.message || error?.message || 'Failed to suggest concept filters. Please try again.';
        this.notificationService.error('Concept filter', message);
      },
    });
  }

  toggleSuggestion(item: SuggestionRow): void {
    item.selected = !item.selected;
  }

  toggleGroup(group: { group: string; items: SuggestionRow[] }, checked: boolean): void {
    group.items.forEach((item) => {
      item.selected = checked;
    });
  }

  isGroupFullySelected(group: { items: SuggestionRow[] }): boolean {
    return group.items.length > 0 && group.items.every((i) => i.selected);
  }

  isGroupPartiallySelected(group: { items: SuggestionRow[] }): boolean {
    const selectedCount = group.items.filter((i) => i.selected).length;
    return selectedCount > 0 && selectedCount < group.items.length;
  }

  handleOk(): void {
    if (!this.canOk) {
      return;
    }

    const filters = this.selectedSuggestions.map((s) => s.filter);
    this.dialogRef.close({
      filters,
      combineWith: this.combineWith,
    });
  }

  handleCancel(): void {
    this.dialogRef.close(undefined);
  }

  toggleSaveForm(): void {
    this.showSaveForm = !this.showSaveForm;
    if (this.showSaveForm && !this.saveName.trim() && this.lastConcept) {
      this.saveName = this.lastConcept.slice(0, 60);
    }
  }

  handleSave(): void {
    if (!this.saveName.trim() || !this.selectedSuggestions.length) {
      this.notificationService.error('Save concept', 'Name and at least one selected filter are required.');
      return;
    }

    const concept = (this.lastConcept || this.conceptText).trim();
    if (concept.length < 3) {
      this.notificationService.error('Save concept', 'Concept text must be at least 3 characters.');
      return;
    }

    this.loadingSave = true;
    // Persist the same DataFilter shape analytics already uses (normalized, no UI-only fields).
    const filters: Types.ConceptDataFilter[] = this.selectedSuggestions.map((s) => {
      const normalized = normalizeConceptDataFilter(s.filter);
      return {
        logicalOperator:
          normalized.logicalOperator === 'AND' || normalized.logicalOperator === 'OR'
            ? normalized.logicalOperator
            : 'OR',
        columnName: normalized.columnName,
        operationType: normalized.operationType,
        value: normalized.value,
        isClusterFilter: normalized.isClusterFilter,
        clusterFilters: normalized.clusterFilters,
      };
    });
    const selectedSuggestionIds = this.selectedSuggestions.map((s) => s.id);

    const persist = (createdByEmail?: string) => {
      const body: Types.ConceptFilterSaveRequest = {
        name: this.saveName.trim().slice(0, 200),
        concept,
        filters,
        isGlobal: this.isGlobal !== false,
      };

      const description = this.saveDescription.trim();
      if (description) {
        body.description = description;
      }
      if (this.phrases.length) {
        body.phrases = this.phrases;
      }
      if (selectedSuggestionIds.length) {
        body.selectedSuggestionIds = selectedSuggestionIds;
      }
      if (createdByEmail) {
        body.createdByEmail = createdByEmail;
      }

      this.commonService.saveConceptFilter(body).subscribe({
        next: () => {
          this.loadingSave = false;
          this.notificationService.success(
            'Concept filter',
            this.isGlobal ? 'Saved as a global concept filter.' : 'Saved as a private concept filter.'
          );
          this.showSaveForm = false;
        },
        error: (error) => {
          this.loadingSave = false;
          this.notificationService.error('Save concept', this.extractApiError(error));
        },
      });
    };

    this.cognitoService
      .getUser()
      .then((user) => persist(user?.email))
      .catch(() => persist(undefined));
  }

  /** Prefer FastAPI `detail` (and nested validation errors) over Angular's generic Http failure text. */
  private extractApiError(error: any): string {
    const status = error?.status;
    if (status === 403) {
      return 'You do not have permission to save this concept filter.';
    }
    if (status === 409) {
      return error?.error?.detail || 'A concept filter with this name already exists.';
    }

    const detail = error?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail) && detail.length) {
      return detail
        .map((item) => item?.msg || item?.message || JSON.stringify(item))
        .join('; ');
    }
    if (error?.error?.message) {
      return error.error.message;
    }
    return error?.message || 'Failed to save concept filter.';
  }

  private rebuildGroups(): void {
    const map = new Map<string, SuggestionRow[]>();
    for (const suggestion of this.suggestions) {
      const key = suggestion.group || suggestion.column || 'Other';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(suggestion);
    }
    this.groupedSuggestions = Array.from(map.entries()).map(([group, items]) => ({ group, items }));
  }
}
